import React, { useEffect } from 'react';
import { Entity } from '@backstage/catalog-model';
import {
  DefaultEntityFilters,
  EntityFilter,
  EntityKindPicker,
  EntityLifecyclePicker,
  EntityNamespacePicker,
  EntityOwnerPicker,
  EntityProcessingStatusPicker,
  EntityTagPicker,
  EntityTypePicker,
  UserListPicker,
  useEntityList,
} from '@backstage/plugin-catalog-react';

/**
 * Hide the `moneyforward`-namespace duplicates of Component/API entities from the
 * `/catalog` list only. Product Master (`/dfp/product-master`) and entity detail
 * pages (`/catalog/:namespace/:kind/:name`) are unaffected — they use their own
 * catalog queries / a separate route, not this list page.
 */
export const isHiddenMoneyforwardEntity = (entity: Entity): boolean => {
  const namespace = entity.metadata.namespace ?? 'default';
  return (
    namespace === 'moneyforward' &&
    (entity.kind === 'Component' || entity.kind === 'API')
  );
};

class HideMoneyforwardComponentsApisFilter implements EntityFilter {
  // ponytail: client-side filter. Correct because this CatalogIndexPage is not
  // server-paginated. If `pagination` is ever enabled, move to a server-side filter.
  filterEntity(entity: Entity): boolean {
    return !isHiddenMoneyforwardEntity(entity);
  }
}

type CatalogListFilterState = DefaultEntityFilters & {
  hideMoneyforwardComponentsApis?: EntityFilter;
};

/** Registers the always-on hide filter inside CatalogIndexPage's EntityListProvider. */
const ApplyHideMoneyforward = (): null => {
  const { updateFilters } = useEntityList<CatalogListFilterState>();
  useEffect(() => {
    updateFilters({
      hideMoneyforwardComponentsApis: new HideMoneyforwardComponentsApisFilter(),
    });
  }, [updateFilters]);
  return null;
};

/**
 * Default catalog sidebar pickers + the always-on hide filter. Pass as
 * `CatalogIndexPage`'s `filters` prop so `CatalogIndexPage` stays a direct
 * routable element in App.tsx (required for the `catalog` routeRef to bind).
 * The `filters` node is rendered inside CatalogFilterLayout.Filters, so these
 * are the raw pickers (no wrapper).
 */
export const CatalogListFilters = () => (
  <>
    <EntityKindPicker />
    <EntityTypePicker />
    <UserListPicker initialFilter="owned" />
    <EntityOwnerPicker />
    <EntityLifecyclePicker />
    <EntityTagPicker />
    <EntityProcessingStatusPicker />
    <EntityNamespacePicker />
    <ApplyHideMoneyforward />
  </>
);
